import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react'
import { ISaveDraftCallback } from '../Models/CategoryFormData'

interface UnsavedChangesContextType {
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
  saveDraftCallback: ISaveDraftCallback
  setSaveDraftCallback: (cb: ISaveDraftCallback) => void
  pendingNavigation: string | null
  setPendingNavigation: (path: string | null) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null)

export const UnsavedChangesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [saveDraftCallback, setSaveDraftCallback] = useState<ISaveDraftCallback>(undefined)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)

  const handleSetIsDirty = useCallback((dirty: boolean) => {
    setIsDirty(dirty)
  }, [])

  const handleSetSaveDraftCallback = useCallback((cb: ISaveDraftCallback) => {
    setSaveDraftCallback(() => cb)
  }, [])

  const handleSetPendingNavigation = useCallback((path: string | null) => {
    setPendingNavigation(path)
  }, [])

  const contextValue = useMemo(
    () => ({
      isDirty,
      setIsDirty: handleSetIsDirty,
      saveDraftCallback,
      setSaveDraftCallback: handleSetSaveDraftCallback,
      pendingNavigation,
      setPendingNavigation: handleSetPendingNavigation,
    }),
    [
      isDirty,
      handleSetIsDirty,
      saveDraftCallback,
      handleSetSaveDraftCallback,
      pendingNavigation,
      handleSetPendingNavigation,
    ],
  )

  return (
    <UnsavedChangesContext.Provider value={contextValue}>{children}</UnsavedChangesContext.Provider>
  )
}

export const useUnsavedChanges = (): UnsavedChangesContextType => {
  const context = useContext(UnsavedChangesContext)
  if (!context) {
    return {
      isDirty: false,
      setIsDirty: () => {
        return
      },
      saveDraftCallback: undefined,
      setSaveDraftCallback: () => {
        return
      },
      pendingNavigation: null,
      setPendingNavigation: () => {
        return
      },
    }
  }
  return context
}
